import { useAccount } from '@/domain/hooks/useAccount'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { useQuery } from '@tanstack/react-query'
import { type Address, erc20Abi, formatUnits } from 'viem'
import { useConfig } from 'wagmi'
import { readContract } from 'wagmi/actions'

/**
 * Fetches the ERC20 balance for a single token.
 * Used for on-demand balance loading of Lifi-only tokens
 * that aren't covered by the lending pool's batch balance fetch.
 */
export function useTokenBalance({
  tokenAddress,
  decimals,
  chainId,
  enabled = true,
}: {
  tokenAddress: Address | undefined
  decimals: number
  chainId: number
  enabled?: boolean
}) {
  const account = useAccount()
  const wagmiConfig = useConfig()

  return useQuery({
    queryKey: ['token-balance', tokenAddress, account, chainId],
    queryFn: async () => {
      if (!account || !tokenAddress) return NormalizedUnitNumber(0)

      const balance = await readContract(wagmiConfig, {
        abi: erc20Abi,
        address: tokenAddress,
        functionName: 'balanceOf',
        args: [account],
        chainId,
      })

      return NormalizedUnitNumber(formatUnits(balance, decimals))
    },
    enabled: enabled && !!account && !!tokenAddress,
    staleTime: 30_000,
  })
}
